const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export interface Anime {
  id: number;
  mal_id?: number;
  title: string;
  poster: string;
  score: number;
  type: string;
  episodes: number;
  status: string;
  synopsis: string;
  genres: { name: string }[] | string[];
  year?: number;
  cover?: string;
  isAdult?: boolean;
}

export interface Episode {
  number: number;
  title: string;
  id: string;
  image?: string;
}

export interface Relation {
  id: number;
  malId: number;
  title: string;
  poster: string;
  type: string;
  relationType: string;
  episodes: number;
  year: number;
  format: string;
}

export interface EmbedServer {
  name: string;
  url: string;
  type: 'embed' | 'download' | 'hls';
  external?: boolean;
}

const GENRES = [
  'Action', 'Adventure', 'Comedy', 'Drama', 'Fantasy', 'Horror',
  'Mecha', 'Mystery', 'Romance', 'Sci-Fi', 'Slice of Life', 'Sports',
  'Supernatural', 'Thriller',
];

export function getGenres(): string[] {
  return GENRES;
}

async function gqlFetch(query: string, vars?: Record<string, any>): Promise<any> {
  for (let i = 0; i < 3; i++) {
    try {
      const r = await fetch('https://graphql.anilist.co', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: vars || {} }),
      });
      const d = await r.json();
      if (d.errors) {
        const isRate = d.errors.some((e: any) => e.message?.toLowerCase().includes('rate')) || r.status === 429;
        if (isRate && i < 2) { await new Promise(r => setTimeout(r, 1000 * (i + 1))); continue; }
        throw new Error(d.errors[0].message);
      }
      return d;
    } catch (e: any) {
      if (i < 2 && (e.message?.includes('rate') || e.message?.includes('timeout'))) {
        await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        continue;
      }
      if (i === 2) { console.warn('gqlFetch failed after 3 retries:', e.message); throw e; }
    }
  }
}

function mapAL(m: any): Anime | null {
  if (!m) return null;
  const t = m.title;
  return {
    id: m.id,
    mal_id: m.idMal || 0,
    title: t?.english || t?.romaji || '',
    poster: m.coverImage?.extraLarge || m.coverImage?.large || '',
    score: m.averageScore ? m.averageScore / 10 : 0,
    type: m.format || '',
    episodes: m.episodes || 0,
    status: m.status || '',
    synopsis: (m.description || '').replace(/<[^>]*>/g, ''),
    genres: m.genres || [],
    year: m.seasonYear || 0,
    cover: m.bannerImage || m.coverImage?.extraLarge || m.coverImage?.large || '',
    isAdult: m.isAdult || false,
  };
}

function mapJikan(a: any): Anime | null {
  if (!a) return null;
  const att = a.attributes || a;
  const img = att.images?.jpg?.large_image_url || '';
  const id = att.mal_id || a.id;
  if (!id) return null;
  return {
    id,
    mal_id: id,
    title: att.title || '',
    poster: img,
    score: att.score || 0,
    type: att.type || '',
    episodes: att.episodes || 0,
    status: att.status || '',
    synopsis: (att.synopsis || '').replace(/<[^>]*>/g, ''),
    genres: att.genres || [],
    year: att.year || 0,
    cover: att.images?.jpg?.large_image_url || '',
  };
}

function deduplicate(items: Anime[]): Anime[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const key = item.title.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const PAGE_30 = 'id idMal title{english romaji}coverImage{large}bannerImage averageScore format episodes status genres seasonYear isAdult';

export async function fetchTrending(page = 1, perPage = 30): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,sort:TRENDING_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchTrending failed:', e); return []; }
}

export async function fetchPopular(page = 1, perPage = 50): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,sort:POPULARITY_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchPopular failed:', e); return []; }
}

export async function fetchLatest(page = 1, perPage = 50): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,sort:TRENDING_DESC,status:RELEASING){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchLatest failed:', e); return []; }
}

export async function fetchTopRated(page = 1, perPage = 50): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,sort:SCORE_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchTopRated failed:', e); return []; }
}

export async function fetchSeasonal(page = 1, perPage = 50): Promise<Anime[]> {
  try {
    const year = new Date().getFullYear();
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,seasonYear:${year},sort:POPULARITY_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchSeasonal failed:', e); return []; }
}

export async function fetchMovies(page = 1, perPage = 30): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,format:movie,sort:TRENDING_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchMovies failed:', e); return []; }
}

export async function fetchOvas(page = 1, perPage = 30): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,format:OVA,sort:TRENDING_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchOvas failed:', e); return []; }
}

export async function fetchOnas(page = 1, perPage = 30): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int){Page(page:$p,perPage:${perPage}){media(type:ANIME,format:ONA,sort:TRENDING_DESC){${PAGE_30}}}}`, { p: page });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchOnas failed:', e); return []; }
}

export async function fetchByGenre(genre: string, page = 1, perPage = 30): Promise<Anime[]> {
  try {
    const d = await gqlFetch(`query($p:Int,$g:String){Page(page:$p,perPage:${perPage}){media(type:ANIME,genre:$g,sort:TRENDING_DESC){${PAGE_30}}}}`, { p: page, g: genre });
    return ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('fetchByGenre failed:', e); return []; }
}

function parseRelations(raw: any): Relation[] {
  if (!raw?.relations?.edges) return [];
  return raw.relations.edges
    .map((e: any) => {
      const n = e.node;
      if (!n) return null;
      const t = n.title || {};
      return {
        id: n.id || 0,
        malId: n.idMal || 0,
        title: t.english || t.romaji || '',
        poster: n.coverImage?.large || '',
        type: n.format || '',
        relationType: e.relationType || '',
        episodes: n.episodes || 0,
        year: n.seasonYear || 0,
        format: n.format || '',
      };
    })
    .filter((r: Relation | null): r is Relation =>
      r !== null && r.id > 0 && ['SEQUEL', 'PREQUEL', 'PARENT', 'SPIN_OFF', 'SIDE_STORY'].includes(r.relationType)
    );
}

const DETAIL_FIELDS = 'id idMal title{english romaji}coverImage{large}bannerImage averageScore format episodes status description season seasonYear genres isAdult relations{edges{relationType node{id idMal title{english romaji}coverImage{large}format episodes status seasonYear averageScore}}}';

export async function fetchAnimeDetail(id: number): Promise<{ anime: Anime | null; malId: number; anilistId: number; raw: any; relations: Relation[] }> {
  let anime: Anime | null = null;
  let rawAl: any = null;
  let anilistId = 0, malId = 0;
  let relations: Relation[] = [];

  try {
    const d = await gqlFetch(`query($id:Int){Media(id:$id,type:ANIME){${DETAIL_FIELDS}}}`, { id });
    rawAl = d?.data?.Media;
    if (rawAl) { anime = mapAL(rawAl); anilistId = rawAl.id; malId = rawAl.idMal || 0; relations = parseRelations(rawAl); }
  } catch (e) { console.warn('fetchAnimeDetail: AniList query failed:', e); }

  if (!anime?.title) {
    try {
      const r = await fetch(`${API_BASE}/api/anime?id=${id}`);
      const rd = await r.json();
      if (rd.data) { anime = mapJikan(rd.data); malId = id; anilistId = id; }
    } catch (e) { console.warn('fetchAnimeDetail: Jikan fallback failed:', e); }
  }

  if (!anime?.title) {
    try {
      const d = await gqlFetch(`query($idMal:Int){Media(idMal:$idMal,type:ANIME){${DETAIL_FIELDS}}}`, { idMal: id });
      rawAl = d?.data?.Media;
      if (rawAl) { anime = mapAL(rawAl); anilistId = rawAl.id; malId = rawAl.idMal || 0; relations = parseRelations(rawAl); }
    } catch (e) { console.warn('fetchAnimeDetail: idMal fallback failed:', e); }
  }

  return { anime, malId, anilistId, raw: rawAl, relations };
}

export async function fetchEpisodes(anilistId: number, malId: number): Promise<Episode[]> {
  let eps: Episode[] = [];

  try {
    const p = await gqlFetch(`query($id:Int){Media(id:$id,type:ANIME){id episodes streamingEpisodes{title thumbnail}}}`, { id: anilistId });
    const m = p?.data?.Media;
    if (m?.streamingEpisodes?.length) {
      eps = m.streamingEpisodes.map((e: any, i: number) => ({ number: i + 1, title: e.title || `Episode ${i + 1}`, image: e.thumbnail || '', id: `ep_${anilistId}_${i + 1}` }));
    } else if (m?.episodes) {
      for (let i = 1; i <= m.episodes; i++) eps.push({ number: i, title: `Episode ${i}`, id: `ep_${anilistId}_${i}` });
    }
  } catch (e) { console.warn('fetchEpisodes: AniList failed:', e); }

  if (!eps.length && malId) {
    try {
      const r = await fetch(`${API_BASE}/api/episodes?id=${malId}`);
      const rd = await r.json();
      eps = (rd.data || []).map((e: any) => ({ number: e.mal_id || e.number, title: e.title || `Episode ${e.mal_id || e.number}`, id: `ep_${anilistId}_${e.mal_id || e.number}`, image: e.images?.jpg?.image_url || '' }));
    } catch (e) { console.warn('fetchEpisodes: Jikan fallback failed:', e); }
  }

  return eps;
}

export async function searchAnime(q: string): Promise<Anime[]> {
  let items: Anime[] = [];

  try {
    const d = await gqlFetch(`query($q:String){Page(page:1,perPage:30){media(type:ANIME,search:$q,sort:SEARCH_MATCH){${PAGE_30}}}}`, { q });
    items = ((d?.data?.Page?.media) || []).map(mapAL).filter(Boolean);
  } catch (e) { console.warn('searchAnime: AniList failed:', e); }

  if (!items.length) {
    try {
      const r = await fetch(`${API_BASE}/api/anime?q=${encodeURIComponent(q)}`);
      if (!r.ok) throw new Error(`Jikan API returned ${r.status}`);
      const rd = await r.json();
      items = (rd.data || []).map(mapJikan).filter(Boolean);
    } catch (e) { console.warn('searchAnime: Jikan fallback failed:', e); }
  }

  return deduplicate(items);
}

const serverCache = new Map<string, EmbedServer[]>();

export async function fetchEmbedServers(malId: number, epNum: number, title?: string, anilistId?: number): Promise<EmbedServer[]> {
  const key = `${malId}_${epNum}`;
  const cached = serverCache.get(key);
  if (cached) return cached;
  try {
    let u = `/api/resolve-embed?mal_id=${malId}&ep=${epNum}&lang=sub`;
    if (title) u += `&title=${encodeURIComponent(title)}`;
    if (anilistId) u += `&anilist_id=${anilistId}`;
    const res = await fetch(u);
    const data = await res.json();
    if (data.servers && data.servers.length > 0) {
      const servers = data.servers.map((s: any) => ({
        name: s.name || 'Stream',
        url: s.url || '',
        type: (s.type || 'embed') as EmbedServer['type'],
        external: !!s.external,
      }));
      serverCache.set(key, servers);
      return servers;
    }
    if (data.error) console.warn('resolveEmbed:', data.error);
  } catch (e) { console.warn('fetchEmbedServers failed:', e); }
  return [];
}

export async function fetchDownloadUrls(malId: number, epNum: number): Promise<EmbedServer[]> {
  const key = `${malId}_${epNum}`;
  const cached = serverCache.get(key);
  const servers = cached || await (async () => {
    try {
      const res = await fetch(`/api/resolve-embed?mal_id=${malId}&ep=${epNum}&lang=sub`);
      const data = await res.json();
      if (data.servers && data.servers.length > 0) {
        const sv = data.servers.map((s: any) => ({
          name: s.name || 'Stream', url: s.url || '', type: 'embed' as const, external: !!s.external,
        }));
        serverCache.set(key, sv);
        return sv;
      }
    } catch (e) { console.warn('fetchDownloadUrls failed:', e); }
    return [];
  })();
  if (servers.length > 0) {
    const first = servers[0];
    const url = (first.url || '').replace('/stream/', '/download/');
    return [{ name: first.name || 'Download', url, type: 'download' }];
  }
  return [];
}